import React, { useState, useEffect } from 'react';
import api from '../api';
import { getAllLessons } from '../data/unitsData';
import { FaTrash, FaKey } from 'react-icons/fa';
import PageLayout from '../components/PageLayout'; 

const ManageStudentsPage = () => {
    const [users, setUsers] = useState([]);
    const [editing, setEditing] = useState(null); 
    const [allLessons] = useState(getAllLessons());

    const fetchUsers = async () => {
        try {
            const { data } = await api.get('/users');
            setUsers(data);
        } catch (e) {
            console.error("Failed to load users");
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        
        // FIX: Send data to allowedCourses (Legacy) to ensure DB acceptance
        const payload = {
            name: editing.name,
            lessonCredits: editing.lessonCredits,
            assignedLesson: editing.assignedLesson,
            allowedCourses: editing.allowedCourses || editing.enrolledCourses, 
            enrolledCourses: editing.enrolledCourses || editing.allowedCourses, 
            hasFullAccess: editing.hasFullAccess,
            contactInfo: editing.contactInfo
        };

        if (editing.newPassword && editing.newPassword.trim() !== "") {
            payload.password = editing.newPassword;
        }

        try {
            if (editing.id) {
                await api.put(`/users/${editing.id}`, payload);
            } else {
                await api.post('/users', { ...payload, username: editing.username, password: editing.newPassword, role: 'student' });
            }
            setEditing(null);
            fetchUsers();
            alert("Saved successfully!");
        } catch (err) {
            alert("Error saving user.");
        }
    };

    const deleteUser = async (id) => {
        if(window.confirm("Delete this user?")) {
            await api.delete(`/users/${id}`);
            fetchUsers();
        }
    };

    return (
        <PageLayout>
            <div className="flex justify-between mb-6">
                <h1 className="text-3xl font-bold">User Management</h1>
                <button onClick={() => setEditing({ role: 'student', contactInfo: {}, allowedCourses: [] })} className="bg-green-600 text-white px-4 py-2 rounded font-bold">+ New Student</button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {users.map(u => (
                    <div key={u.id} className="bg-white p-4 rounded shadow border relative">
                        <h3 className="font-bold text-lg">{u.name} <span className="text-xs bg-gray-200 px-1 rounded">{u.role}</span></h3>
                        <p className="text-sm text-gray-500">@{u.username}</p>
                        <p className="text-sm">Credits: {u.lessonCredits}</p>
                        <p className={`text-xs font-bold uppercase mt-2 ${u.status === 'active' ? 'text-green-600' : 'text-orange-500'}`}>{u.status}</p>
                        
                        <div className="mt-4 flex gap-2">
                            <button onClick={() => setEditing({...u, newPassword: ''})} className="flex-1 bg-blue-100 text-blue-700 py-1 rounded text-sm font-bold">Edit</button>
                            <button onClick={() => deleteUser(u.id)} className="bg-red-100 text-red-600 p-2 rounded"><FaTrash/></button>
                        </div>
                    </div>
                ))}
            </div>

            {/* EDIT MODAL */}
            {editing && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <form onSubmit={handleSave} className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">{editing.id ? 'Edit User' : 'Create User'}</h2>
                        
                        {!editing.id && (
                            <div className="mb-4">
                                <label className="block text-xs font-bold">Username</label>
                                <input className="w-full border p-2 rounded" value={editing.username || ''} onChange={e=>setEditing({...editing, username: e.target.value})} required />
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-xs font-bold">Full Name</label>
                                <input className="w-full border p-2 rounded" value={editing.name || ''} onChange={e=>setEditing({...editing, name: e.target.value})} required />
                            </div>
                            <div>
                                <label className="block text-xs font-bold">Credits</label>
                                <input type="number" className="w-full border p-2 rounded" value={editing.lessonCredits || 0} onChange={e=>setEditing({...editing, lessonCredits: e.target.value})} />
                            </div>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded border border-yellow-200 mb-4">
                            <label className="block text-xs font-bold text-yellow-800 flex items-center gap-2"><FaKey/> {editing.id ? 'Reset Password' : 'Set Password'}</label>
                            <input 
                                type="text" 
                                className="w-full border p-2 rounded mt-1" 
                                placeholder={editing.id ? "Leave blank to keep current" : "Required"} 
                                value={editing.newPassword || ''} 
                                onChange={e=>setEditing({...editing, newPassword: e.target.value})} 
                                required={!editing.id}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-xs font-bold mb-1">Assign Lesson</label>
                            <select className="w-full border p-2 rounded" value={editing.assignedLesson || ''} onChange={e=>setEditing({...editing, assignedLesson: e.target.value})}>
                                <option value="">None</option>
                                {allLessons.map(l => <option key={l.fileName} value={l.fileName}>{l.label}</option>)}
                            </select>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-gray-600">Cancel</button>
                            <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded font-bold">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </PageLayout>
    );
};

export default ManageStudentsPage;